import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react"
import { supabase } from "../lib/supabaseClient"

export type Plan = "free" | "basic" | "plus" | "premium"
export type Role = "user" | "admin" | "mentor"

type Profile = {
  id: string
  email: string | null
  full_name: string | null
  role: Role | string
  plan: Plan
  plan_status: string | null
  stripe_customer_id: string | null
}

type AuthState = {
  loading: boolean
  session: any | null
  user: any | null
  profile: Profile | null
  // kept for backward compat — same as profile.plan
  subscription: null
  entitlements: Set<string>
  plan: Plan
  role: Role | string
  refresh: () => Promise<void>
  refreshUser: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthState | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [session, setSession] = useState<any | null>(null)
  const [user, setUser] = useState<any | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)

  const plan: Plan = (profile?.plan as Plan) ?? "free"
  const role = profile?.role ?? "user"

  const loadProfile = useCallback(async (uid: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id,email,full_name,role,plan,plan_status,stripe_customer_id")
      .eq("id", uid)
      .maybeSingle()

    if (error) {
      console.error("profiles load error:", error.message)
    } else if (!data) {
      // No profile row yet — create default
      const authUser = (await supabase.auth.getUser()).data.user
      const defaults = {
        id: uid,
        email: authUser?.email ?? null,
        full_name: authUser?.user_metadata?.full_name ?? null,
        role: "user",
        plan: "free",
        plan_status: "active",
      }
      await supabase.from("profiles").insert(defaults)
      setProfile({ ...defaults, stripe_customer_id: null } as Profile)
    } else {
      setProfile(data as Profile)
    }
  }, [])

  const refresh = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.auth.getSession()
    const nextSession = data.session ?? null
    setSession(nextSession)
    setUser(nextSession?.user ?? null)
    if (nextSession?.user?.id) {
      await loadProfile(nextSession.user.id)
    } else {
      setProfile(null)
    }
    setLoading(false)
  }, [loadProfile])

  // alias for DashboardPage which calls refreshUser
  const refreshUser = refresh

  useEffect(() => {
    refresh()

    const { data: sub } = supabase.auth.onAuthStateChange(
      async (_event, newSession) => {
        setSession(newSession)
        setUser(newSession?.user ?? null)
        if (newSession?.user?.id) {
          await loadProfile(newSession.user.id)
        } else {
          setProfile(null)
        }
        setLoading(false)
      }
    )

    // Safety timeout — never get stuck loading forever if Supabase doesn't respond
    const safety = setTimeout(() => setLoading(false), 6000)

    return () => {
      sub.subscription.unsubscribe()
      clearTimeout(safety)
    }
  }, [refresh, loadProfile])

  const signOut = async () => { await supabase.auth.signOut() }

  const value = useMemo<AuthState>(
    () => ({
      loading, session, user, profile,
      subscription: null,
      entitlements: new Set<string>(),
      plan, role, refresh, refreshUser, signOut,
    }),
    [loading, session, user, profile, plan, role, refresh, refreshUser]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within AuthProvider")
  return ctx
}
